import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import Pagination from '@/components/base/Pagination';
import { categories, getComicsByCategory } from '@/mocks/comics';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  const filteredComics = useMemo(() => getComicsByCategory(activeCategory), [activeCategory]);
  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const paginatedComics = useMemo(
    () => filteredComics.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filteredComics, currentPage]
  );

  const activeCategoryName = categories.find((c) => c.slug === activeCategory)?.name || '全部';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategoryName}韩漫 - 韩漫画网`,
    description: `韩漫画网${activeCategoryName}韩漫分类，提供海量${activeCategoryName}题材韩国漫画免费在线阅读。`,
    url: `https://hanmanhua.com/category/${activeCategory}`,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: filteredComics.length,
      itemListElement: filteredComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'ComicStory',
          name: comic.title,
          url: `https://hanmanhua.com/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: activeCategoryName },
                ]}
              />
            </div>

            <div className="mb-8">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
                {activeCategoryName}韩漫
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {filteredComics.length} 部{activeCategoryName !== '全部' ? activeCategoryName : ''}韩漫作品
              </p>
            </div>

            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-2 px-1 py-1 bg-background-100 rounded-full w-fit">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => {
                      setActiveCategory(cat.slug);
                      setCurrentPage(1);
                      window.history.replaceState(null, '', `/category/${cat.slug}`);
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {paginatedComics.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 mb-10">
                  {paginatedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} size="small" />
                  ))}
                </div>
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}